// Dialer keyboard map (SPEC §15.1). Pure: resolveHotkey() turns a key press + screen context into an action, so the
// rules (no hotkeys while typing, dispositions only after the call, O-then-number chords, keypad digits) are unit-tested.
import type { DispositionCode } from '../../../src/shared/api';
import type { CallPhase } from './machine';
import { DISPOSITION_BY_KEY } from './labels';

export type HotkeyAction =
  | { type: 'dial' }
  | { type: 'hangup' }
  | { type: 'mute' }
  | { type: 'keypad' }
  | { type: 'drop' }
  | { type: 'liveVm' }
  | { type: 'book' }
  | { type: 'text' }      // T: the Text panel for the lead on screen (2026-10-03), any phase
  | { type: 'skip' }
  | { type: 'callBack' }  // C: "Call back now" (2026-10-06): log the call if owed, then redial the same number
  | { type: 'otherLine' } // A: the other line (PJ 2026-10-23): "Call the store" on the card; in the wrap-up, log and call the other number
  | { type: 'objectionChord' }
  | { type: 'objection'; index: number }
  | { type: 'dispose'; code: DispositionCode }
  | { type: 'save' }        // Shift+Enter: save the outcome and load the next lead, without dialing it
  | { type: 'saveDial' }    // Enter: save the outcome, load the next lead and dial it (one human keypress; REVIEW-2026-10-01)
  | { type: 'dtmf'; digit: string }
  | { type: 'escape' }
  | { type: 'help' }
  | { type: 'drawer' }    // I: toggle the Business drawer (LEADGEN-SPEC §11), any phase
  | { type: 'recheck' };  // R: re-run the drawer's live check, only while it is open

export interface KeyLike { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean; shiftKey?: boolean; repeat?: boolean }

export interface HotkeyContext {
  phase: CallPhase;
  chord: 'objection' | null;   // 'O' was pressed and a number is expected
  keypadOpen: boolean;
  /** The Business drawer is open (R re-runs its live check). Optional: absent = closed. */
  drawerOpen?: boolean;
}

/** Elements where a key press is text entry (or picks an option), so dialer hotkeys must stay quiet. */
export interface TargetLike { tagName?: string; type?: string; isContentEditable?: boolean; getAttribute?: (n: string) => string | null }

const NON_TEXT_INPUTS = new Set(['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color', 'file', 'image']);

export function isTypingTarget(t: TargetLike | null | undefined): boolean {
  if (!t || !t.tagName) return false;
  const tag = t.tagName.toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUTS.has((t.type ?? 'text').toLowerCase());
  if (t.isContentEditable) return true;
  const role = t.getAttribute?.('role');
  return role === 'textbox' || role === 'combobox' || role === 'searchbox';
}

const DIGITS = new Set(['0', '1', '2', '3', '4', '5', '6', '7', '8', '9']);
const DTMF = new Set([...DIGITS, '*', '#']);

/** Keys after O, in card order: 1-9, 0 (10th), - (11th), = (12th). Cards past the 12th are click-only. */
export const OBJECTION_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '-', '='] as const;

/** The key that opens objection card `index` (0-based) after O, or null when that card is click-only. */
export function objectionKey(index: number): string | null {
  return OBJECTION_KEYS[index] ?? null;
}

/** null = not a dialer hotkey in this context (the browser keeps its default behaviour). */
export function resolveHotkey(e: KeyLike, ctx: HotkeyContext): HotkeyAction | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  if (e.repeat) return null; // a held key must never re-dial or re-save
  const key = e.key.length === 1 ? e.key.toUpperCase() : e.key;
  const live = ctx.phase === 'connecting' || ctx.phase === 'ringing' || ctx.phase === 'in_call';

  if (key === 'Escape') return { type: 'escape' };
  if (key === '?') return { type: 'help' };

  // Keypad open during a live call: digits, * and # go to the far end as DTMF, nothing else is swallowed.
  if (ctx.keypadOpen && live && DTMF.has(key)) return { type: 'dtmf', digit: key };

  // O then a key: show that objection card (1-9, 0 = 10th, - = 11th, = = 12th).
  if (ctx.chord === 'objection') {
    const index = (OBJECTION_KEYS as readonly string[]).indexOf(key);
    if (index >= 0) return { type: 'objection', index };
  }
  if (key === 'O') return { type: 'objectionChord' };

  // The Business drawer is non-modal: I and R never take H, M, K, O or the outcome keys away. Verify is a click (V drops).
  if (key === 'I') return { type: 'drawer' };
  if (key === 'R') return ctx.drawerOpen ? { type: 'recheck' } : null;

  if (key === ' ' || key === 'D') return ctx.phase === 'idle' || ctx.phase === 'blocked' ? { type: 'dial' } : null;
  if (key === 'H') return live || ctx.phase === 'preparing' ? { type: 'hangup' } : null;
  if (key === 'M') return live ? { type: 'mute' } : null;
  if (key === 'K') return live ? { type: 'keypad' } : null;
  if (key === 'V') return ctx.phase === 'in_call' ? { type: 'drop' } : null;
  if (key === 'L') return { type: 'liveVm' };
  if (key === 'B') return { type: 'book' };
  if (key === 'T') return { type: 'text' };
  if (key === 'S') return ctx.phase === 'idle' || ctx.phase === 'blocked' ? { type: 'skip' } : null;
  // C: in the wrap-up it saves and redials; on the lead card (idle / blocked) it redials a call already saved. The store
  // decides whether a call back is on offer (and says why not); never during a call.
  if (key === 'C') return ctx.phase === 'needs_disposition' || ctx.phase === 'idle' || ctx.phase === 'blocked' ? { type: 'callBack' } : null;
  // A (PJ 2026-10-23; S is Skip): "Call the store" on the lead card; in the wrap-up, "Call the store now" after the
  // owner's line, "Call the owner now" after the store line. The store decides whether there is another line.
  if (key === 'A') return ctx.phase === 'needs_disposition' || ctx.phase === 'idle' || ctx.phase === 'blocked' ? { type: 'otherLine' } : null;

  if (ctx.phase === 'needs_disposition') {
    if (key === 'Enter') return e.shiftKey ? { type: 'save' } : { type: 'saveDial' };
    const code = DISPOSITION_BY_KEY[key];
    if (code) return { type: 'dispose', code };
  }
  return null;
}

/** The dialer's keydown rule in one place: nothing while typing in a box, otherwise resolveHotkey. */
export function dialerKeyAction(e: KeyLike, target: TargetLike | null | undefined, ctx: HotkeyContext): HotkeyAction | null {
  if (isTypingTarget(target)) return null;
  return resolveHotkey(e, ctx);
}

/**
 * "Call this number again?" (PJ 2026-10-20): Enter or Y = "Yes, call", Esc or N = "No". The dialog takes these keys
 * before anything else (a capture listener that stops them), so they never reach the dialer's hotkeys or the dial pad.
 * 'swallow' = one of its keys held down (repeat): stopped, does nothing, so the Space or Enter that opened the dialog
 * can never answer it. Enter on a focused button is left to the button (Tab to No, Enter = No). Modifier combinations
 * and every other key are not the dialog's (null): the dialer's own handler stays quiet while a dialog is open.
 */
export function confirmKeyAction(e: KeyLike, target?: TargetLike | null): 'yes' | 'no' | 'swallow' | null {
  if (e.ctrlKey || e.metaKey || e.altKey) return null;
  const key = e.key.length === 1 ? e.key.toUpperCase() : e.key;
  const action = key === 'Enter' || key === 'Y' ? 'yes' : key === 'Escape' || key === 'N' ? 'no' : null;
  if (!action) return null;
  if (e.repeat) return 'swallow';
  if (key === 'Enter' && (target?.tagName ?? '').toUpperCase() === 'BUTTON') return null;
  return action;
}

/** For the shortcuts overlay. */
export const HOTKEY_HELP: Array<[string, string]> = [
  ['Space / D', "Dial this lead (you press it for every call; nothing dials on its own). A lead with the owner's number calls the owner's line first. Called recently? It reads Call again and asks \"Call this number again?\" first: Enter or Y = yes, Esc or N = no"],
  ['A', "Call the store: dials the store line instead of the owner's. After a call, logs it (No answer if you picked nothing) and calls the other line right away: the store after the owner, the owner after the store"],
  ['H', 'Hang up'],
  ['M', 'Mute / unmute'],
  ['K', 'Keypad for phone menus and extensions (digits then go to the call)'],
  ['V', 'Drop your approved voicemail (only when allowed)'],
  ['L', 'Show the live voicemail script'],
  ['B', 'Book a meeting'],
  ['T', 'Text this lead from our number (during or after the call)'],
  ['O then 1-9, 0, -, =', 'Show an objection answer (cards 1-12; any after that are click-only)'],
  ['1, 2, 3, 5, 7, 8, 9, 0, X', 'Pick the call outcome (after the call; 4 also picks Gatekeeper, 6 Callback)'],
  ['Enter', 'Save the outcome and dial the next lead (your keypress dials it; nothing dials on its own)'],
  ['Shift+Enter', 'Save the outcome and load the next lead without dialing'],
  ['C', 'Call back now: hung up on or cut off? Logs the call and redials the same number right away, within 15 minutes (never more than 3 calls a day to one number)'],
  ['S', 'Skip this lead without calling'],
  ['I', 'Business info: Google profile, website, reviews, people'],
  ['R', 'Re-check the business live (only while Business info is open)'],
  ['Esc', 'Close a panel'],
  ['?', 'Show these shortcuts'],
];
